import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { TrendingUp, AttachMoney, ShoppingCart, People } from '@mui/icons-material';
import { repAPI } from '../../services/api';

const RepAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [clientBreakdown, setClientBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const [dashboardRes, breakdownRes] = await Promise.all([
        repAPI.getRepAnalytics(),
        repAPI.getRepClientBreakdown()
      ]);
      
      if (dashboardRes.success) {
        setAnalytics(dashboardRes.data);
      }
      if (breakdownRes.success) {
        setClientBreakdown(breakdownRes.data);
      }
    } catch (err) {
      setError('Failed to load analytics');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Sales Analytics
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {analytics && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom>Total Clients</Typography>
                      <Typography variant="h4">{analytics.totalClients}</Typography>
                    </Box>
                    <People fontSize="large" color="primary" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom>Total Orders</Typography>
                      <Typography variant="h4">{analytics.totalOrders}</Typography>
                    </Box>
                    <ShoppingCart fontSize="large" color="primary" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom>Total Revenue</Typography>
                      <Typography variant="h4">${analytics.totalOrderValue.toFixed(0)}</Typography>
                    </Box>
                    <AttachMoney fontSize="large" color="success" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom>Avg Order Value</Typography>
                      <Typography variant="h4">${analytics.averageOrderValue.toFixed(0)}</Typography>
                    </Box>
                    <TrendingUp fontSize="large" color="info" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Client Performance</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Client</TableCell>
                    <TableCell align="right">Orders</TableCell>
                    <TableCell align="right">Total Ordered</TableCell>
                    <TableCell align="right">Total Paid</TableCell>
                    <TableCell align="right">Unpaid</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clientBreakdown.map((client) => (
                    <TableRow key={client.clientId}>
                      <TableCell>{client.clinicName || client.clientEmail}</TableCell>
                      <TableCell align="right">{client.totalOrders}</TableCell>
                      <TableCell align="right">${client.totalOrdered.toFixed(2)}</TableCell>
                      <TableCell align="right">${client.totalPaid.toFixed(2)}</TableCell>
                      <TableCell align="right">${client.totalUnpaid.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default RepAnalytics;

